import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { Calculator, Copy, Download, FileSpreadsheet, PlusCircle } from "lucide-react";
import { toast } from "react-toastify";

import useListController from "@/hooks/list/useListController";
import { oaKey, useOADuplicate, useOAList, useOALookups } from "@/hooks/planning/oa/useOA";
import { useScreenPermissions } from "@/hooks/general/useScreenPermissions";
import { SCREEN_ID_OA } from "@/constants/screenIds";
import { validRoles } from "@/services/Utils";

import useFilterStore from "@/stores/useFilterStore";
import useUserStore from "@/stores/useUserStore";

import { useOAColumns } from "../data/useOAColumns";
import { useOADataTransform } from "../data/useOADataTransform";
import { useOAExport } from "../data/useOAExport";
import { useOAFilters } from "../filters/useOAFilters";

import { oaBaseFilter, oaBaseTable } from "../../oa.constants";

export default function useOAController({ initialBaseFilter = oaBaseFilter, skipAutoDateInit = false } = {}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [selectedRows, setSelectedRows] = useState({});
	const [showCopyOAConfirm, setShowCopyOAConfirm] = useState(false);
	const filter = useFilterStore((state) => state.filter);
	const customer = useUserStore((state) => state.user?.customer);
	const { estate_parent_id, estate_id, search, parameter } = filter;

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const canShowPrice = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const { canShow, canCreate, isLoading: isLoadingPermissions, permissions } = useScreenPermissions(SCREEN_ID_OA);
	const canLoadScreenData = !isLoadingPermissions && canShow;

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		applyFilterOverrides,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: initialBaseFilter,
		baseTable: oaBaseTable,
	});

	const showInternalCode = Boolean(customer?.use_code_oa);

	const { data: lookups, isLoading: isLoadingLookups } = useOALookups({ enabled: canLoadScreenData });

	const { isReady } = useOAFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
		showInternalCode,
		skipAutoDateInit,
	});

	const queryFilters = useMemo(
		() => ({
			...appliedFilterParams,
			page: tableState.page,
			per_page: tableState.per_page,
			estate_parent_id,
			estate_id,
			search,
			parameter,
		}),
		[appliedFilterParams, tableState.page, tableState.per_page, estate_parent_id, estate_id, search, parameter],
	);

	const {
		data: response,
		isLoading,
		isFetching,
	} = useOAList(queryFilters, {
		enabled: canLoadScreenData && Boolean(estate_id) && !isLoadingLookups && Boolean(lookups) && isReady,
	});
	const { mutate: duplicateApplicationOrders, isLoading: isDuplicatingApplicationOrders } = useOADuplicate();
	const tableData = useOADataTransform(response?.data || []);

	useEffect(() => {
		setSelectedRows({});
	}, [response?.data]);

	const handleRowSelect = useCallback((rowIndex, rowData, checked) => {
		setSelectedRows((previousState) => {
			if (checked) {
				return { ...previousState, [rowIndex]: { ...rowData } };
			}

			const nextState = { ...previousState };
			delete nextState[rowIndex];
			return nextState;
		});
	}, []);

	const handleSelectAll = useCallback(
		(checked) => {
			if (!checked) {
				setSelectedRows({});
				return;
			}

			const nextState = {};
			tableData.forEach((rowData, index) => {
				nextState[String(index)] = { ...rowData };
			});
			setSelectedRows(nextState);
		},
		[tableData],
	);

	const selectedOrderIds = useMemo(
		() =>
			Object.values(selectedRows)
				.map((row) => Number(row.app_order_id || row.id || 0))
				.filter(Boolean),
		[selectedRows],
	);

	const handleCalculateNecessity = useCallback(() => {
		if (selectedOrderIds.length === 0) {
			return;
		}
		navigate(`/planificacion/ordenes_aplicacion?oa=${selectedOrderIds.join(",")}&action=calcular_necesidad`);
	}, [navigate, selectedOrderIds]);

	const handleDuplicateSelectedOA = useCallback(() => {
		if (selectedOrderIds.length === 0) {
			return;
		}

		duplicateApplicationOrders(
			{
				app_order_ids: selectedOrderIds,
				filters: queryFilters,
			},
			{
				onSuccess: (response) => {
					const newCodes = response?.new_codes || [];
					const message =
						newCodes.length === 1
							? `Se ha duplicado correctamente. Nueva OA N° ${newCodes[0]}`
							: `Se duplicaron ${newCodes.length} OAs correctamente. Nuevos códigos: ${newCodes.join(", ")}`;
					toast.success(message);
					queryClient.invalidateQueries(oaKey);
					setSelectedRows({});
					setShowCopyOAConfirm(false);
				},
				onError: () => {
					toast.error("Error al duplicar las OAs. Por favor intente nuevamente.");
				},
			},
		);
	}, [duplicateApplicationOrders, queryClient, queryFilters, selectedOrderIds]);

	const { handleExportOA, handleExportFieldBook } = useOAExport(appliedFilterParams, tableState);

	const handleCreateOA = useCallback(() => {
		navigate("/orden_aplicacion/ingresar");
	}, [navigate]);

	const selectActions = useMemo(() => {
		return [
			{
				label: `Crear copia de ${selectedOrderIds.length} OA(s)`,
				icon: "Copy",
				variant: "primary",
				onClick: () => setShowCopyOAConfirm(true),
			},
			{
				label: `Calcular necesidad para ${selectedOrderIds.length} OA(s)`,
				icon: "Calculator",
				variant: "info",
				onClick: handleCalculateNecessity,
			},
		];
	}, [handleCalculateNecessity, selectedOrderIds.length]);

	const selectionState = useMemo(
		() => ({
			selectedRows,
			onRowSelect: handleRowSelect,
			onSelectAll: handleSelectAll,
			allRowsCount: tableData.length,
		}),
		[handleRowSelect, handleSelectAll, selectedRows, tableData.length],
	);

	const columns = useOAColumns(selectionState, navigate, customer, {
		permissions,
		canShowPrice,
	});

	const tableActionButtons = useMemo(
		() => [
			{
				key: "actions",
				label: "Acciones",
				icon: FileSpreadsheet,
				options: [
					{
						key: "export_oa",
						label: "Exportar Órdenes de Aplicación",
						icon: Download,
						onClick: handleExportOA,
					},
					{
						key: "export_field_book",
						label: "Exportar Cuaderno de Campo",
						icon: Download,
						onClick: handleExportFieldBook,
					},
					{
						key: "new_oa",
						label: "Nueva OA",
						icon: PlusCircle,
						onClick: handleCreateOA,
					},
				],
			},
		],
		[handleCreateOA, handleExportFieldBook, handleExportOA],
	);

	return {
		navigate,
		isLoadingPermissions,
		canShow,
		canCreate,
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		reactTableSort,
		isLoading: isLoading || isLoadingLookups || !isReady,
		isFetching,
		tableData,
		response,
		columns,
		selectActions,
		selectActionIconMap: { Calculator, Copy },
		selectedRowsCount: Object.keys(selectedRows).length,
		tableActionButtons,
		showCopyOAConfirm,
		setShowCopyOAConfirm,
		handleDuplicateSelectedOA,
		handleCreateOA,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		applyFilterOverrides,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		isDuplicatingApplicationOrders,
	};
}
